import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { getProjectJobsQuery } from '@/queries/project.query';
import { layersQuery } from '@/queries/layer.query';
import { rainfallTrackingAtom } from '@/atoms/rainfall-progress.atom';

/**
 * Mounts once per project shell (alongside `useDeviationProgress`,
 * `useGroundClassifyProgress`, `useClipboxExportProgress`).
 *
 * Same shape as the ground-classify tracker hook:
 *
 * 1. Generic project-jobs query filtered by `batchId` for failure detection.
 * 2. Layers query for success detection — postprocess sets
 *    `metadata.derivedFrom` on the GROUP + TILE + VECTOR children, so any
 *    new derived layer is sufficient to resolve the toast.
 *
 * Polling cadence: explicit `setInterval(..., 2000)` driven by
 * `hasTrackers`, calling `refetch()` on both queries directly. Same
 * rationale as `useDeviationProgress` — shared query keys with
 * page-level subscribers make `refetchInterval` flaky.
 */
export function useRainfallProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(rainfallTrackingAtom);
  const queryClient = useQueryClient();

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  const { data: layersData, refetch: refetchLayers } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    // `cancelRefetch: false`: `refetch()` cancels the request still in
    // flight by default, so a response slower than this 2 s interval is
    // never delivered and the toast spins for ever. A tick JOINS the poll
    // already running (see `useDeviationProgress` for the reported case).
    const tick = () => {
      void refetchJobs({ cancelRefetch: false });
      void refetchLayers({ cancelRefetch: false });
    };
    // Fire immediately so the user doesn't wait 2 s after clicking Run.
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [hasTrackers, projectId, refetchJobs, refetchLayers]);

  useEffect(() => {
    if (!hasTrackers) return;

    const jobs = jobsData?.jobs ?? [];
    const layers = layersData?.layers ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        // Failure path: any job in the batch hit FAILED. The rainfall
        // batch contains a single RAINFALL_ANALYSIS leaf job, so a
        // batchId match is sufficient.
        const jobInBatch = jobs.find((j) => j.batchId === tracker.batchId);
        if (jobInBatch?.status === 'FAILED') {
          toast.error(t('rainfallAnalysis.failedToast', { name: tracker.sourceLayerName }), {
            id: tracker.toastId,
            duration: 5000,
          });
          next.splice(i, 1);
          changed = true;
          continue;
        }

        // Success path: a layer with `metadata.derivedFrom ===
        // sourceLayerId` that wasn't in the pre-run snapshot has
        // appeared. postprocess sets derivedFrom on the GROUP, the TILE
        // child, and the VECTOR child — any of them resolves the toast.
        const existingIds = new Set(tracker.existingDerivedLayerIds);
        const newLayer = layers.find((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string };
          if (meta.derivedFrom !== tracker.sourceLayerId) return false;
          return !existingIds.has(l.id);
        });
        if (newLayer) {
          toast.success(
            t('rainfallAnalysis.completedToast', { name: tracker.sourceLayerName }),
            { id: tracker.toastId, duration: 5000 },
          );
          queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [jobsData, layersData, hasTrackers, projectId, queryClient, setTrackers, t]);
}
